import { useEffect, useState } from "react";

const T = {
  navy: "#0A1E45",
  navy2: "#071530",
  gold: "#C9A540",
  gold2: "#E4C87A",
  white: "#FFFFFF",
  offwhite: "#FAFAF7",
  cream: "#F5F1E8",
  light: "#EEE9DF",
  muted: "#64647A",
  border: "#DDD8CE",
  ffHead: "'Playfair Display', Georgia, serif",
  ffBody: "'DM Sans', system-ui, sans-serif",
};

const SCP_FORM_URL = "https://admissions.eusbd.eu/scp-apply/";
const CORP_FORM_URL = "https://admissions.eusbd.eu/corporate-apply/";
const SCP_PARTNER_PACK_URL = "/documents/ESBD%20SCP%20Partner%20Pack%20v1.zip";

const scopedCSS = `
  .scp-wrap { font-family: ${T.ffBody}; font-size: 16px; color: ${T.navy}; background: ${T.white}; -webkit-font-smoothing: antialiased; }
  .scp-wrap * { box-sizing: border-box; }
  .scp-wrap a { color: inherit; }
  .scp-wrap .scp-trustbar-inner { flex-wrap: wrap; }
  @media (min-width: 901px) {
    .scp-wrap .scp-trustbar-inner { flex-wrap: nowrap !important; }
  }
  .scp-wrap .esbd-hero-eyebrow { animation: slideIn 0.5s 0.1s ease both; }
  .scp-wrap .esbd-hero-h1 { animation: slideIn 0.6s 0.2s ease both; }
  .scp-wrap .esbd-hero-sub { animation: slideIn 0.6s 0.3s ease both; }
  .scp-wrap .esbd-hero-actions { animation: slideIn 0.6s 0.4s ease both; }
  .scp-wrap .esbd-hero-stats { animation: fadeUp 0.7s 0.35s ease both; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideIn { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
  .scp-wrap .esbd-benefit-card::before { content: ''; position: absolute; top: 0; left: 0; width: 3px; height: 100%; background: ${T.gold}; transform: scaleY(0); transform-origin: bottom; transition: transform 0.25s; }
  .scp-wrap .esbd-benefit-card:hover::before { transform: scaleY(1); }
  .scp-wrap .esbd-benefit-card:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(10,30,69,0.1); }
  .scp-wrap .esbd-dl-item:hover { border-color: ${T.navy}; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(10,30,69,0.08); }
  .scp-wrap .esbd-faq-btn { width: 100%; text-align: left; background: none; border: none; padding: 22px 0; cursor: pointer; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; font-family: ${T.ffBody}; font-size: 15px; font-weight: 600; color: ${T.navy}; line-height: 1.45; transition: color 0.15s; }
  .scp-wrap .esbd-faq-btn:hover { color: ${T.gold}; }
  .scp-wrap .esbd-tier-card:hover { background: rgba(255,255,255,0.07); }
  .scp-wrap .esbd-partner-card { transition: transform 0.3s, box-shadow 0.3s; }
  .scp-wrap .esbd-partner-card:hover { transform: translateY(-4px); box-shadow: 0 20px 60px rgba(10,30,69,0.1); }
  @media (max-width: 980px) {
    .scp-wrap .scp-hero-grid { grid-template-columns: 1fr !important; gap: 28px !important; }
    .scp-wrap .scp-benefits-grid { grid-template-columns: 1fr !important; }
    .scp-wrap .scp-elig-grid { grid-template-columns: 1fr !important; }
    .scp-wrap .scp-process-grid { grid-template-columns: 1fr !important; }
    .scp-wrap .scp-downloads-grid { grid-template-columns: 1fr !important; }
    .scp-wrap .scp-cta-grid { grid-template-columns: 1fr !important; }
    .scp-wrap .scp-faq-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
  }
  @media (max-width: 600px) {
    .scp-wrap > section { padding-left: 20px !important; padding-right: 20px !important; }
    .scp-wrap #apply { padding-left: 20px !important; padding-right: 20px !important; }
    .scp-wrap .scp-hero-grid { gap: 42px !important; }
    .scp-wrap .scp-trustbar-inner { padding-left: 16px !important; padding-right: 16px !important; }
    .scp-wrap .scp-trustbar-item { padding: 14px 12px 14px 8px !important; }
    .scp-wrap .scp-trustbar-inner { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
    .scp-wrap .scp-trustbar-item { border-right: none !important; border-bottom: 1px solid rgba(10,30,69,0.15) !important; }
    .scp-wrap .scp-trustbar-item:last-child { border-bottom: none !important; }

    /* Role Definition: mobile par panels stack ho */
    .scp-wrap .scp-role-definition-grid { grid-template-columns: 1fr !important; }
    .scp-wrap .scp-role-panel { padding: 32px 20px !important; }
    .scp-wrap .scp-role-list-item { font-size: 14px !important; padding: 10px 0 !important; gap: 12px !important; }
  }
`;

const Eyebrow = ({ children, light = false, center = false }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      color: light ? T.gold2 : T.gold,
      letterSpacing: "0.24em",
      textTransform: "uppercase",
      marginBottom: 16,
      textAlign: center ? "center" : "left",
    }}
  >
    {children}
  </div>
);

const SectionTitle = ({ children, light = false, center = false, style: extra }) => (
  <h2
    style={{
      fontFamily: T.ffHead,
      fontSize: "clamp(30px, 3.8vw, 46px)",
      fontWeight: 700,
      lineHeight: 1.15,
      color: light ? T.white : T.navy,
      marginBottom: 16,
      textAlign: center ? "center" : "left",
      ...extra,
    }}
  >
    {children}
  </h2>
);

const Lead = ({ children, light = false, center = false }) => (
  <p
    style={{
      fontSize: 17,
      color: light ? "rgba(255,255,255,0.6)" : T.muted,
      lineHeight: 1.8,
      maxWidth: 580,
      marginBottom: 60,
      textAlign: center ? "center" : "left",
      margin: center ? "0 auto 60px" : "0 0 60px",
    }}
  >
    {children}
  </p>
);

const TrustBar = () => (
  <div style={{ background: T.gold }}>
    <div className="scp-trustbar-inner" style={{ maxWidth: 1200, margin: "0 auto", padding: "0 40px", display: "flex", alignItems: "stretch" }}>
      {["MFHEA Accreditation Pending", "EQF Referenced", "EU Member State", "Multiple Programmes", "Online Delivery"].map((item, i, a) => (
        <div
          key={i}
          className="scp-trustbar-item"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "16px 20px 16px 12px",
            borderRight: i < a.length - 1 ? "1px solid rgba(10,30,69,0.15)" : "none",
            fontSize: 11,
            fontWeight: 700,
            color: T.navy,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            flex: "0 0 auto",
          }}
        >
          <span style={{ width: 6, height: 6, background: T.navy, borderRadius: "50%", opacity: 0.4 }} />
          {item}
        </div>
      ))}
    </div>
  </div>
);

const BENEFITS = [
  { n: "01", title: "Tiered Commission — 10 to 18%", body: "Your rate rises with every student you bring each intake. Plus early-bird, quality and growth bonuses on top of your base commission." },
  { n: "02", title: "Dedicated Partner Portal", body: "Track applications, manage your pipeline, issue student voucher codes and access marketing assets from a purpose-built dashboard." },
  { n: "03", title: "Co-Branding & City Listing", body: "Your organisation appears on the ESBD website as an official study centre partner, backed by Malta's EU-regulated institutional framework." },
  { n: "04", title: "Monthly Marketing Kits", body: "Ready-to-deploy marketing materials, webinar decks, social copy and approved brand assets delivered every intake cycle." },
  { n: "05", title: "Scholarship Access", body: "Offer students Africa, Asia, Leadership Excellence and other scholarship discounts — making the programme more accessible in your market." },
  { n: "06", title: "Teaching Centre Pathway", body: "Sustain strong KPIs over two intakes and you become eligible to progress to ESBD Teaching Centre status — subject to MFHEA approval." },
];

const TIERS = [
  { tier: "Tier 1", range: "1–4 students", rate: "10%", note: "Standard entry rate", gold: false },
  { tier: "Tier 2", range: "5–9 students", rate: "12%", note: "Growth partner rate", gold: false },
  { tier: "Tier 3", range: "10–19 students", rate: "15%", note: "+€750 Growth Bonus", gold: true },
  { tier: "Tier 4", range: "20+ students", rate: "18%", note: "+€2,000 Bonus · Excl. eligible", gold: true },
];

const CAN_DO = [
  "Recruit and market using ESBD-approved materials",
  "Host monthly information sessions from your premises",
  "Advise students on applications and payment options",
  "Issue ESBD voucher codes to prospective students",
  "Facilitate national qualification recognition locally",
  "Offer independent supplementary tuition independently",
  "Report pipeline through the ESBD Partner Portal",
];

const CANNOT_DO = [
  "Deliver timetabled teaching from ESBD modules",
  "Set, mark or invigilate any ESBD assessments",
  "Make admissions decisions on ESBD's behalf",
  "Claim academic credit for partner-run events",
  "Call premises a campus or teaching centre",
  "Accept student tuition payments for ESBD",
  "Use ESBD branding outside approved guidelines",
];

const ELIG = [
  { n: "01", title: "Legal Status & Governance", body: "Registered legal entity with clear ownership and a named responsible officer. No unresolved regulatory sanctions." },
  { n: "02", title: "Leadership & Staffing", body: "Named partnership lead and at least one trained student adviser. CPD commitment in place." },
  { n: "03", title: "Facilities & Safety", body: "Accessible venue for information sessions. Basic health, safety and safeguarding arrangements." },
  { n: "04", title: "Marketing Capability", body: "Active digital presence, events track record and ability to follow ESBD brand guidelines." },
  { n: "05", title: "Student Support", body: "Enquiry handling within 48 hours, document checks within 72 hours, complaints process in place." },
  { n: "06", title: "Data Protection", body: "GDPR-aligned handling, secure storage, deletion on request and an anti-bribery policy." },
  { n: "07", title: "Financial Integrity", body: "Ability to invoice professionally. No side payments to students. Transparent local fees." },
  { n: "08", title: "Strategic Fit", body: "Demonstrable employer links, market demand and alignment with ESBD's target professional audience." },
];

const STEPS = [
  { tag: "Stage One", title: "Submit Your Expression of Interest", body: "Complete the form and submit your Stage 1 documentation. Applications are reviewed on a rolling basis.", items: ["Organisation profile and ownership details", "Key staff CVs and contact list", "Venue overview and marketing reach", "Two professional or university references"] },
  { tag: "Stage Two", title: "Stage 2 Review & Online Meeting", body: "Qualifying organisations provide additional detail and an online review meeting may follow.", items: ["Annual marketing and events plan", "Student care and complaints process", "Data protection and risk summary", "Optional online review with ESBD team"] },
  { tag: "Stage Three", title: "Formal Approval & Onboarding", body: "Approved organisations receive an offer, partner agreement and full onboarding support.", items: ["Formal recognition letter issued", "Partner portal access activated", "Adviser training and SLA briefing", "Co-branding and city listing published"] },
];

const DOWNLOADS = [
  { format: "PDF", title: "SCP Stage 1 — Document Checklist", desc: "Use this checklist to prepare the supporting documents needed to complete your Stage 1 application.", href: "/documents/ESBD%20SCP%20Stage1%20Document%20Checklist%20v1.pdf" },
  { format: "PDF", title: "SCP Recognition Framework", desc: "Assessment criteria, scoring rubric, KPI framework and review process for partners.", href: "/documents/ESBD%20SCP%20Recognition%20Framework%20V1.pdf" },
  { format: "PDF", title: "Partner Benefits & Money Policy", desc: "Full commission structure, bonus framework and payment terms for all partner tiers.", href: "/documents/ESBD%20Partner%20Benefits%20Money%20Policy%20v1.pdf" },
];

const FAQS = [
  { q: "Is ESBD a recognised institution?", a: "ESBD is a Malta-based higher education institution currently pursuing MFHEA accreditation. Our programmes are mapped to MQF Level 7 and referenced to the EQF. Once accreditation is confirmed, this supports recognition across EU member states." },
  { q: "When are the intakes?", a: "Current planned intakes are March 2027 and September 2027. Applications are accepted on a rolling basis ahead of each intake window." },
  { q: "Can we offer classes or teach modules?", a: "No. All teaching, assessment and academic decisions remain with ESBD in Malta. You may independently offer supplementary tuition at your own pricing, but not as ESBD-delivered content." },
  { q: "What is the Teaching Centre pathway?", a: "Outstanding SCPs who sustain strong KPIs over two intakes may apply for Teaching Centre status. This requires a formal application, venue inspection, academic staffing plan and approval by ESBD's Head of Institution, and is subject to MFHEA approval." },
  { q: "Can we have exclusivity in our city?", a: "Yes — city exclusivity is available after two successful intakes meeting KPI and quality thresholds. At that point your commission rises to up to 20%." },
  { q: "How is commission paid?", a: "Commission is calculated on Net Tuition Received and paid by bank transfer within 30 days of the census date for each intake, pro rata on cleared student instalments." },
  { q: "Do partner events carry academic credit?", a: "No. Partner-hosted events are optional for students and carry no ESBD academic credit, MQF level or formal assessment weight." },
  { q: "What support does ESBD provide partners?", a: "Partners receive portal access, monthly marketing kits, webinar decks, approved copy, adviser training, named ESBD contacts with clear SLAs, scholarship allocations and co-branding on the ESBD website." },
];

function useScopedStyles() {
  useEffect(() => {
    const id = "scp-scoped-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = scopedCSS;
    document.head.appendChild(style);
  }, []);
}

export default function SCPPage() {
  useScopedStyles();
  const [open, setOpen] = useState(null);
  const half = Math.ceil(FAQS.length / 2);

  return (
    <div className="scp-wrap">
      <section style={{ background: T.navy, position: "relative", overflow: "hidden", padding: "100px 32px 90px" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 80% at 80% 50%, rgba(201,165,64,0.08) 0%, transparent 70%), radial-gradient(ellipse 40% 60% at -10% 100%, rgba(201,165,64,0.05) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: -60, right: -60, width: 420, height: 420, border: "1px solid rgba(201,165,64,0.12)", borderRadius: "50%", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: -20, right: -20, width: 260, height: 260, border: "1px solid rgba(201,165,64,0.2)", borderRadius: "50%", pointerEvents: "none" }} />

        <div className="scp-hero-grid" style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: "1fr 380px", gap: 80, alignItems: "center" }}>
          <div>
            <div className="esbd-hero-eyebrow" style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, fontWeight: 600, color: T.gold, letterSpacing: "0.22em", textTransform: "uppercase", marginBottom: 24 }}>
              <span style={{ display: "block", width: 32, height: 1, background: T.gold }} />
              Study Centre Partner Programme
            </div>
            <h1 className="esbd-hero-h1" style={{ fontFamily: T.ffHead, fontSize: "clamp(38px, 5vw, 58px)", fontWeight: 900, lineHeight: 1.08, color: T.white, marginBottom: 28 }}>
              Become a
              <br />
              Study Centre Partner.
              <br />
              <em style={{ color: T.gold, fontStyle: "italic", display: "block" }}>Build Something Lasting.</em>
            </h1>
            <p className="esbd-hero-sub" style={{ fontSize: 17, color: "rgba(255,255,255,0.7)", lineHeight: 1.75, maxWidth: 480, marginBottom: 40 }}>
              Join ESBD&apos;s international partner network. Represent a Malta-based MFHEA-regulated master&apos;s degree in your country, earn tiered commission on every student you enrol, and grow with us into a recognised Teaching Centre.
            </p>
            <div className="esbd-hero-actions" style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>

              <a target="blank" href="https://admissions.eusbd.eu/scp-apply/" style={{ background: T.gold, color: T.navy, fontFamily: T.ffBody, fontSize: 14, fontWeight: 700, padding: "14px 32px", borderRadius: 3, textDecoration: "none", letterSpacing: "0.05em", textTransform: "uppercase", border: `2px solid ${T.gold}` }}>
                Express Interest
              </a>
              <a href={SCP_PARTNER_PACK_URL} download="ESBD SCP Partner Pack v1.zip" style={{ background: "transparent", color: "rgba(255,255,255,0.85)", fontFamily: T.ffBody, fontSize: 14, fontWeight: 500, padding: "14px 32px", borderRadius: 3, textDecoration: "none", border: "1px solid rgba(255,255,255,0.25)" }}>
                Download Partner Pack
              </a>
            </div>
          </div>

          <div className="esbd-hero-stats" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,165,64,0.25)", padding: 36, backdropFilter: "blur(8px)" }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.gold, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 28, paddingBottom: 16, borderBottom: "1px solid rgba(201,165,64,0.2)" }}>
              Partnership at a glance
            </div>
            {[{ num: "10–18%", label: "Tiered Commission Rate" }, { num: "26+", label: "Target Partner Countries" }, { num: "2×", label: "Intakes Per Year" }, { num: "EU", label: "Malta-Based EU Institution" }].map(({ num, label }, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", padding: "18px 0", borderBottom: i < 3 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                <span style={{ fontFamily: T.ffHead, fontSize: 36, fontWeight: 700, color: T.gold, lineHeight: 1, marginBottom: 4 }}>{num}</span>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TrustBar />

      <section id="benefits" style={{ padding: "100px 40px", background: T.white }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow>Why Partner with ESBD</Eyebrow>
          <SectionTitle>A partnership that pays — in more ways than one</SectionTitle>
          <Lead>Everything you need to build a credible, profitable education recruitment operation in your market.</Lead>
          <div className="scp-benefits-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
            {BENEFITS.map(({ n, title, body }) => (
              <div key={n} className="esbd-benefit-card" style={{ background: T.white, border: `1px solid ${T.border}`, padding: "36px 30px", position: "relative", overflow: "hidden", transition: "transform 0.2s, box-shadow 0.2s" }}>
                <div style={{ fontFamily: T.ffHead, fontSize: 56, fontWeight: 900, color: T.cream, lineHeight: 1, marginBottom: -8 }}>{n}</div>
                <div style={{ fontFamily: T.ffHead, fontSize: 18, fontWeight: 700, color: T.navy, marginBottom: 12, lineHeight: 1.3 }}>{title}</div>
                <div style={{ fontSize: 14, color: T.muted, lineHeight: 1.75 }}>{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="commission" style={{ padding: "100px 40px", background: T.navy }}>
        <div className="scp-hero-grid" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 80, alignItems: "start" }}>
          <div>
            <Eyebrow light>Commercial Model</Eyebrow>
            <SectionTitle light>The more you recruit, the more you earn</SectionTitle>
            <Lead light>Commission is calculated on Net Tuition Received per enrolled learner, paid after the census date.</Lead>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {TIERS.map(({ tier, range, rate, note, gold }) => (
                <div key={tier} className="esbd-tier-card" style={{ display: "grid", gridTemplateColumns: "120px 1fr auto", alignItems: "center", gap: 20, padding: "20px 24px", background: gold ? "rgba(201,165,64,0.1)" : "rgba(255,255,255,0.04)", border: `1px solid ${gold ? "rgba(201,165,64,0.3)" : "rgba(255,255,255,0.06)"}` }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.4)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{tier}</div>
                  <div>
                    <div style={{ fontSize: 13, color: gold ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.7)" }}>{range}</div>
                    <div style={{ fontSize: 11, color: gold ? "rgba(201,165,64,0.7)" : "rgba(255,255,255,0.35)" }}>{note}</div>
                  </div>
                  <div style={{ fontFamily: T.ffHead, fontSize: 36, fontWeight: 700, color: gold ? T.gold2 : T.gold, lineHeight: 1, textAlign: "right" }}>{rate}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
              {[{ label: "Early-Bird", val: "€100", desc: "Per student, max 10" }, { label: "Quality Bonus", val: "€500", desc: "Per intake at threshold" }].map(({ label, val, desc }) => (
                <div key={label} style={{ background: "rgba(201,165,64,0.08)", border: "1px solid rgba(201,165,64,0.2)", padding: "16px 18px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: T.gold, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontFamily: T.ffHead, fontSize: 20, fontWeight: 700, color: T.white }}>{val}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 3 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ padding: 32, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", marginBottom: 16 }}>
              <Eyebrow light>City Exclusivity</Eyebrow>
              <div style={{ fontFamily: T.ffHead, fontSize: 20, fontWeight: 700, color: T.white, marginBottom: 10, lineHeight: 1.3 }}>Available after two successful intakes</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.75 }}>Partners who sustain two consecutive intakes meeting KPI and quality thresholds earn exclusive recruitment rights in their city, with commission rising to up to 20%.</div>
            </div>
            <div style={{ background: T.gold, padding: "28px 32px" }}>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(10,30,69,0.6)", marginBottom: 8 }}>Currently Available Programme</div>
              <div style={{ fontFamily: T.ffHead, fontSize: 18, fontWeight: 700, color: T.navy, lineHeight: 1.3, marginBottom: 6 }}>Master of Arts in Global Business &amp; Strategic Leadership</div>
              <div style={{ fontSize: 12, color: "rgba(10,30,69,0.55)", fontStyle: "italic" }}>More programmes will be unveiled. Watch this space.</div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: "100px 40px", background: T.white }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow>Role Definition</Eyebrow>
          <SectionTitle>Crystal clear on what you do and what you don&apos;t</SectionTitle>
          <Lead>All teaching and academic decisions stay with ESBD. Your role is recruitment, community and student support.</Lead>
          <div className="scp-role-definition-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, background: T.border }}>
            <div className="scp-role-panel" style={{ background: T.white, padding: "48px 44px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", paddingBottom: 20, marginBottom: 24, borderBottom: `2px solid ${T.border}`, color: T.navy }}>✓ &nbsp;What Study Centre Partners Do</div>
              <ul className="scp-role-list" style={{ listStyle: "none" }}>
                {CAN_DO.map((item, i) => (
                  <li key={i} className="scp-role-list-item" style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: i < CAN_DO.length - 1 ? `1px solid ${T.light}` : "none", fontSize: 14.5, lineHeight: 1.6, color: T.navy }}>
                    <span style={{ color: "#2DAE6D", fontSize: 17, flexShrink: 0 }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="scp-role-panel" style={{ background: T.navy, padding: "48px 44px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", paddingBottom: 20, marginBottom: 24, borderBottom: "2px solid rgba(201,165,64,0.25)", color: T.gold }}>✕ &nbsp;What Study Centre Partners Do Not Do</div>
              <ul className="scp-role-list" style={{ listStyle: "none" }}>
                {CANNOT_DO.map((item, i) => (
                  <li key={i} className="scp-role-list-item" style={{ display: "flex", gap: 14, padding: "12px 0", borderBottom: i < CANNOT_DO.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none", fontSize: 14.5, lineHeight: 1.6, color: "rgba(255,255,255,0.75)" }}>
                    <span style={{ color: "#E05252", fontSize: 17, flexShrink: 0 }}>✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="eligibility" style={{ padding: "100px 40px", background: T.cream }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow>Who Should Apply</Eyebrow>
          <SectionTitle>Basic eligibility requirements</SectionTitle>
          <Lead>We welcome education agencies, business schools, professional institutes, innovation hubs and community organisations with credible employer links.</Lead>
          <div className="scp-elig-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, background: T.border, border: `1px solid ${T.border}` }}>
            {ELIG.map(({ n, title, body }) => (
              <div key={n} style={{ background: T.white, padding: 32, display: "flex", gap: 20 }}>
                <div style={{ fontFamily: T.ffHead, fontSize: 40, fontWeight: 900, color: T.cream, lineHeight: 1, flexShrink: 0, minWidth: 48 }}>{n}</div>
                <div>
                  <div style={{ fontFamily: T.ffHead, fontSize: 16, fontWeight: 700, color: T.navy, marginBottom: 6, lineHeight: 1.3 }}>{title}</div>
                  <div style={{ fontSize: 13.5, color: T.muted, lineHeight: 1.7 }}>{body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="process" style={{ background: T.navy }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 40px 40px" }}>
          <Eyebrow light>How It Works</Eyebrow>
          <SectionTitle light>A straightforward, three-stage process</SectionTitle>
        </div>
        <div className="scp-process-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)" }}>
          {STEPS.map(({ tag, title, body, items }, i) => (
            <div key={i} style={{ padding: "48px 40px", borderRight: i < 2 ? "1px solid rgba(255,255,255,0.06)" : "none", position: "relative" }}>
              <div style={{ position: "absolute", top: 32, right: 28, fontFamily: T.ffHead, fontSize: 72, fontWeight: 900, color: "rgba(255,255,255,0.04)", lineHeight: 1 }}>0{i + 1}</div>
              <div style={{ display: "inline-block", fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: T.navy, background: T.gold, padding: "4px 12px", marginBottom: 28 }}>{tag}</div>
              <div style={{ fontFamily: T.ffHead, fontSize: 22, fontWeight: 700, color: T.white, marginBottom: 16, lineHeight: 1.25 }}>{title}</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.8, marginBottom: 20 }}>{body}</div>
              <ul style={{ listStyle: "none" }}>
                {items.map((item, j) => (
                  <li key={j} style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", display: "flex", gap: 8, marginBottom: 8 }}>
                    <span style={{ color: T.gold }}>—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section id="downloads" style={{ padding: "100px 40px", background: T.white }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <Eyebrow>Resources</Eyebrow>
          <SectionTitle>Everything you need to get started</SectionTitle>
          <Lead>Download the full partner documentation pack before or after submitting your expression of interest.</Lead>
          <div className="scp-downloads-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {DOWNLOADS.map(({ format, title, desc, href }) => (
              <a key={title} href={href} download className="esbd-dl-item" style={{ background: T.white, border: `1px solid ${T.border}`, padding: "32px 28px", textDecoration: "none", display: "block", transition: "all 0.2s" }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: T.gold, background: T.navy, display: "inline-block", padding: "4px 10px", marginBottom: 16 }}>{format}</div>
                <div style={{ fontFamily: T.ffHead, fontSize: 17, fontWeight: 700, color: T.navy, marginBottom: 8, lineHeight: 1.35 }}>{title}</div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 20 }}>{desc}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, letterSpacing: "0.08em", textTransform: "uppercase" }}>Download ↓</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" style={{ padding: "100px 40px", background: T.cream }}>
        <div style={{ maxWidth: 1060, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <Eyebrow center>Partner With Us</Eyebrow>
            <h2 style={{ fontFamily: T.ffHead, fontSize: 42, fontWeight: 700, color: T.navy, lineHeight: 1.15, marginBottom: 16 }}>
              Two Ways to Work
              <br />
              with <em style={{ color: T.gold, fontStyle: "italic" }}>ESBD</em>
            </h2>
            <p style={{ fontSize: 17, color: T.muted, lineHeight: 1.75, maxWidth: 600, margin: "0 auto" }}>
              Whether you want to represent our programmes locally or explore a strategic corporate collaboration, we have a partnership model that fits.
            </p>
          </div>

          <div className="scp-cta-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
            <div className="esbd-partner-card" style={{ background: T.white }}>
              <div style={{ background: T.navy, padding: "44px 44px 36px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 180, height: 180, border: "1px solid rgba(201,165,64,0.15)", borderRadius: "50%", pointerEvents: "none" }} />
                <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", marginBottom: 20, background: T.gold, color: T.navy }}>Study Centre Partner</span>
                <h3 style={{ fontFamily: T.ffHead, fontSize: 28, fontWeight: 700, color: T.white, lineHeight: 1.2, marginBottom: 12 }}>Represent ESBD<br />in Your Country</h3>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.7 }}>Recruit students, host information sessions, and earn tiered commission on every enrolment. The flagship partnership model.</p>
              </div>
              <div style={{ padding: "40px 44px" }}>
                <ul style={{ listStyle: "none", marginBottom: 32 }}>
                  {["10–18% tiered commission on net tuition", "Dedicated marketing kit and co-branding", "Named ESBD contact with clear SLAs", "Partner portal access and training", "Pathway to Teaching Centre status"].map((f, i, a) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 14.5, color: T.navy, lineHeight: 1.6, padding: "10px 0", borderBottom: i < a.length - 1 ? "1px solid #F3F1EC" : "none" }}>
                      <span style={{ width: 20, height: 20, flexShrink: 0, marginTop: 2, background: T.cream, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.gold, fontWeight: 700 }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href={SCP_FORM_URL} style={{ display: "block", width: "100%", textAlign: "center", padding: "16px 32px", fontFamily: T.ffBody, fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", background: T.gold, color: T.navy, transition: "all 0.25s" }}>
                  Express Interest — Study Centre Partner →
                </a>
              </div>
            </div>

            <div className="esbd-partner-card" style={{ background: T.white }}>
              <div style={{ background: T.navy, padding: "44px 44px 36px", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 180, height: 180, border: "1px solid rgba(201,165,64,0.15)", borderRadius: "50%", pointerEvents: "none" }} />
                <span style={{ display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", padding: "5px 14px", marginBottom: 20, background: "rgba(255,255,255,0.1)", color: T.gold, border: "1px solid rgba(201,165,64,0.3)" }}>Corporate Partnership</span>
                <h3 style={{ fontFamily: T.ffHead, fontSize: 28, fontWeight: 700, color: T.white, lineHeight: 1.2, marginBottom: 12 }}>Strategic Collaboration<br />for Organisations</h3>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.7 }}>Sponsor scholarships, commission staff development programmes, or co-create executive education initiatives with ESBD.</p>
              </div>
              <div style={{ padding: "40px 44px" }}>
                <ul style={{ listStyle: "none", marginBottom: 32 }}>
                  {["12% staff development discount", "Sponsored scholarship programmes", "Bespoke executive education and CPD", "CSR and leadership development initiatives", "Tailored to your organisational goals"].map((f, i, a) => (
                    <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 14.5, color: T.navy, lineHeight: 1.6, padding: "10px 0", borderBottom: i < a.length - 1 ? "1px solid #F3F1EC" : "none" }}>
                      <span style={{ width: 20, height: 20, flexShrink: 0, marginTop: 2, background: T.cream, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: T.gold, fontWeight: 700 }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <a href={CORP_FORM_URL} style={{ display: "block", width: "100%", textAlign: "center", padding: "16px 32px", fontFamily: T.ffBody, fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", textDecoration: "none", background: T.navy, color: T.white, transition: "all 0.25s" }}>
                  Express Interest — Corporate Partner →
                </a>
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 48, fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
            Your information is processed in accordance with EU GDPR. ESBD, based in Malta, is the data controller.{" "}
            <a href="/privacy-policy" style={{ color: T.navy, fontWeight: 600, textDecoration: "none" }}>
              Read our Privacy Policy
            </a>
            .
          </div>
        </div>
      </section>

      <section id="faq" style={{ padding: "100px 40px", background: T.white }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <Eyebrow center>FAQs</Eyebrow>
            <SectionTitle center>Questions we hear most</SectionTitle>
          </div>
          <div className="scp-faq-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 60px" }}>
            {[FAQS.slice(0, half), FAQS.slice(half)].map((col, ci) => (
              <div key={ci}>
                {col.map((item, i) => {
                  const idx = ci * half + i;
                  return (
                    <div key={idx} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <button className="esbd-faq-btn" onClick={() => setOpen(open === idx ? null : idx)}>
                        <span>{item.q}</span>
                        <span style={{ width: 24, height: 24, background: open === idx ? T.gold : T.navy, color: open === idx ? T.navy : T.white, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, transition: "background 0.2s" }}>
                          {open === idx ? "−" : "+"}
                        </span>
                      </button>
                      {open === idx && <div style={{ padding: "0 0 20px", fontSize: 14, color: T.muted, lineHeight: 1.8 }}>{item.a}</div>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ background: T.navy, padding: "80px 40px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 60% 100% at 50% 100%, rgba(201,165,64,0.08), transparent)", pointerEvents: "none" }} />
        <h2 style={{ fontFamily: T.ffHead, fontSize: 40, fontWeight: 700, color: T.white, marginBottom: 14, position: "relative" }}>Ready to Partner with ESBD?</h2>
        <p style={{ fontSize: 17, color: "rgba(255,255,255,0.6)", marginBottom: 36, position: "relative" }}>Join our growing network of partners across Africa, Asia and the Gulf.</p>
        <div style={{ display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", position: "relative" }}>
          <a href={SCP_FORM_URL} style={{ display: "inline-block", background: T.gold, color: T.navy, fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "16px 44px", textDecoration: "none", transition: "background 0.2s" }}>
            Become a Study Centre Partner
          </a>
          <a href={CORP_FORM_URL} style={{ display: "inline-block", background: "transparent", color: "rgba(255,255,255,0.85)", fontSize: 14, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "16px 44px", textDecoration: "none", border: "1px solid rgba(255,255,255,0.3)", transition: "all 0.2s" }}>
            Explore Corporate Partnership
          </a>
        </div>
      </div>
    </div>
  );
}

